"use client";

import React, { useState, useEffect } from "react";
import {
  DownloadCloudIcon,
  Edit,
  Plus,
  Trash2,
  TrashIcon,
  UploadIcon,
  X,
} from "lucide-react";
import BookingTable from "./common/Table";
import Button from "./Masters/Button";
import {
  useGetTourTypeTheme,
  useAddTourTheme,
  useUpdateTourTheme,
  updateTourThemeStatus,
  useDeleteTourThemes,
  useTourBookingType,
} from "@/hooks/useCommon";
import { useAuth } from "@/store/auth";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

interface TourTheme {
  id: number;
  tour_type: number;
  meta_title: string | null;
  meta_description: string | null;
  meta_keywords: string | null;
  theme_name: string;
  icon: string;
  created_date: string;
  created_by: number;
  modified_by: number | null;
  modified_date: string;
  status: number;
  ip: string;
  tour_type_name: string;
  created_by_name: string;
  modified_by_name: string | null;
}

interface BookingType {
  id: number;
  tour_type: string;
}

const TourType: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentTheme, setCurrentTheme] = useState<TourTheme | null>(null);
  const [themes, setThemes] = useState<TourTheme[]>([]);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  // Form states
  const [tourType, setTourType] = useState<string>("");
  const [themeName, setThemeName] = useState<string>("");
  const [metaTitle, setMetaTitle] = useState<string>("");
  const [metaDescription, setMetaDescription] = useState<string>("");
  const [metaKeywords, setMetaKeywords] = useState<string>("");

  // API Hooks
  const getTourThemes = useGetTourTypeTheme();
  const addTourTheme = useAddTourTheme();
  const updateTourTheme = useUpdateTourTheme();
  const updateStatus = updateTourThemeStatus();
  const deleteTourThemes = useDeleteTourThemes();
  const bookingTypesQuery = useTourBookingType();

  const user = useAuth();
  const loggedInUser = user?.user?.id;

  const bookingTypes: BookingType[] = bookingTypesQuery.data?.data || [];

  // Fetch tour themes on component mount
  useEffect(() => {
    fetchTourThemes();
  }, []);

  const fetchTourThemes = () => {
    getTourThemes.mutate(
      {},
      {
        onSuccess: (data) => {
          if (data.status === "success") {
            setThemes(data.data);
          }
        },
        onError: (error) => {
          console.error("Error fetching tour themes:", error);
          toast.error("Failed to fetch tour themes");
        },
      }
    );
  };

  // Reset states when modal opens/closes
  useEffect(() => {
    if (!isModalOpen) {
      setCurrentTheme(null);
      setTourType("");
      setThemeName("");
      setMetaTitle("");
      setMetaDescription("");
      setMetaKeywords("");
    }
  }, [isModalOpen]);

  // Set form data when currentTheme changes (for editing)
  useEffect(() => {
    if (currentTheme && isModalOpen) {
      setTourType(currentTheme.tour_type?.toString() || "");
      setThemeName(currentTheme.theme_name || "");
      setMetaTitle(currentTheme.meta_title || "");
      setMetaDescription(currentTheme.meta_description || "");
      setMetaKeywords(currentTheme.meta_keywords || "");
    }
  }, [currentTheme, isModalOpen]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!tourType) {
      toast.warn("Please select a tour type");
      return;
    }
    if (!themeName.trim()) {
      toast.warn("Please enter a theme name");
      return;
    }

    try {
      const payload = {
        tour_type: parseInt(tourType),
        theme_name: themeName.trim(),
        meta_title: metaTitle.trim() || null,
        meta_description: metaDescription.trim() || null,
        meta_keywords: metaKeywords.trim() || null,
        icon: "default-icon.png", 
        status: 1,
        ip: "127.0.0.1",
        created_by: loggedInUser,
      };

      if (currentTheme) {
        // Update existing tour theme
        updateTourTheme.mutate(
          {
            ...payload,
            auto_id: currentTheme.id,
            modified_by: loggedInUser,
          },
          {
            onSuccess: (data) => {
              if (data.status === "success") {
                toast.success("Tour theme updated successfully");
                fetchTourThemes();
                setIsModalOpen(false);
                setCurrentTheme(null);
              }
            },
            onError: (error) => {
              toast.error("Failed to update tour theme. Please try again.");
            },
          }
        );
      } else {
        // Add new tour theme
        addTourTheme.mutate(payload, {
          onSuccess: (data) => {
            if (data.status === "success") {
              toast.success("Tour theme added successfully");
              fetchTourThemes();
              setIsModalOpen(false);
            }
          },
          onError: (error) => {
            toast.error("Failed to add tour theme. Please try again.");
          },
        });
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    }
  };

  const handlePublish = () => {
    if (selectedRows.length === 0) return;

    updateStatus.mutate(
      {
        id: selectedRows.join(","),
        user_id: loggedInUser,
        status: 1,
      },
      {
        onSuccess: (data) => {
          if (data.status === "success") {
            toast.success("Tour themes published successfully");
            fetchTourThemes();
            setSelectedRows([]);
          }
        },
        onError: (error) => {
          toast.error("Failed to publish tour themes. Please try again.");
        },
      }
    );
  };

  const handleUnpublish = () => {
    if (selectedRows.length === 0) return;

    updateStatus.mutate(
      {
        id: selectedRows.join(","),
        user_id: loggedInUser,
        status: 0,
      },
      {
        onSuccess: (data) => {
          if (data.status === "success") {
            toast.success("Tour themes unpublished successfully");
            fetchTourThemes();
            setSelectedRows([]);
          }
        },
        onError: (error) => {
          toast.error("Failed to unpublish tour themes. Please try again.");
        },
      }
    );
  };

  const handleDelete = async () => {
    if (selectedRows.length === 0) return;

    const message =
      selectedRows.length === 1
        ? "Are you sure you want to delete this tour theme?"
        : `Are you sure you want to delete ${selectedRows.length} selected tour themes?`;

    const result = await Swal.fire({
      title: "Confirm delete",
      text: message,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
    });

    if (!result.isConfirmed) return;

    deleteTourThemes.mutate(
      { id: selectedRows.join(","), user_id: loggedInUser },
      {
        onSuccess: (data) => {
          if (data.status === "success") {
            toast.success("Tour theme(s) deleted successfully");
            fetchTourThemes();
            setSelectedRows([]);
          }
        },
        onError: () => {
          Swal.fire(
            "Error",
            "Failed to delete tour theme(s). Please try again.",
            "error"
          );
        },
      }
    );
  };

  const tableData = themes.map((item, index) => ({
    sno: index + 1,
    id: item.id,
    tourType: item.tour_type_name || `Type ${item.tour_type}`,
    themeName: item.theme_name,
    metaTitle: item.meta_title,
    metaDescription: item.meta_description,
    metaKeywords: item.meta_keywords,
    status: item.status,
    createdBy: item.created_by_name,
    createdDate: item.created_date,
    originalData: item,
  }));

  const columns = [
    {
      key: "selection",
      header: "",
      sortable: false,
      render: (_: any, row: any) => (
        <input
          type="checkbox"
          checked={selectedRows.includes(row.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRows([...selectedRows, row.id]);
            } else {
              setSelectedRows(selectedRows.filter((id) => id !== row.id));
            }
          }}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      ),
    },
    { key: "sno", header: "S.No", sortable: true },
    { key: "tourType", header: "Tour Type", sortable: true },
    { 
      key: "themeName", 
      header: "Theme Name", 
      sortable: true,
      render: (themeName: string) => (
        <div className="max-w-[150px] truncate" title={themeName}>
          {themeName}
        </div>
      ),
    },
    {
      key: "metaTitle",
      header: "Meta Title",
      sortable: true,
      render: (metaTitle: string) => (
        <div className="max-w-[200px] truncate" title={metaTitle || "N/A"}>
          {metaTitle || "N/A"}
        </div>
      ),
    },
    {
      key: "metaDescription",
      header: "Meta Description",
      sortable: true,
      render: (metaDescription: string) => (
        <div 
          className="max-w-[250px] truncate" 
          title={metaDescription || "N/A"}
        >
          {metaDescription || "N/A"}
        </div>
      ),
    },
    {
      key: "metaKeywords",
      header: "Meta Keywords",
      sortable: true,
      render: (metaKeywords: string) => (
        <div className="max-w-[200px] truncate" title={metaKeywords || "N/A"}>
          {metaKeywords || "N/A"}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (status: number) => {
        return (
          <div className="flex items-center">
            <div
              className={`w-3 h-3 rounded-full mr-2 ${
                status === 1 ? "bg-green-500" : "bg-red-500"
              }`}
            />
            <span className="text-sm">
              {status === 1 ? "Active" : "Inactive"}
            </span>
          </div>
        );
      },
    },
    {
      key: "createdBy",
      header: "Created By",
      sortable: true,
      render: (createdByName: string) => createdByName,
    },
    {
      key: "createdDate",
      header: "Created Date",
      sortable: true,
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      key: "action",
      header: "Action",
      render: (_: any, row: any) => {
        return (
          <div className="flex space-x-2">
            <button
              className="text-blue-500 hover:text-blue-700"
              onClick={() => {
                setCurrentTheme(row.originalData);
                setIsModalOpen(true);
              }}
            >
              <Edit size={15} />
            </button>
           
            <button
              className="text-red-500 hover:text-red-700"
              onClick={async () => {
                const result = await Swal.fire({
                  title: "Confirm delete",
                  text: "Are you sure you want to delete this tour theme?",
                  icon: "warning",
                  showCancelButton: true,
                  confirmButtonText: "Yes, delete",
                });

                if (!result.isConfirmed) return;

                deleteTourThemes.mutate(
                  {
                    id: row.id.toString(),
                    user_id: loggedInUser,
                  },
                  {
                    onSuccess: () => {
                      toast.success("Tour theme deleted successfully");
                      fetchTourThemes();
                    },
                    onError: () => {
                      Swal.fire(
                        "Error",
                        "Failed to delete tour theme. Please try again.",
                        "error"
                      );
                    },
                  }
                );
              }}
            >
              <Trash2 size={15} />
            </button>
          </div>
        );
      },
    },
  ];

  // Select all checkbox handler
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedRows(tableData.map((row) => row.id));
    } else {
      setSelectedRows([]);
    }
  };

  const formFields = [
    {
      id: "tour_type",
      label: "Tour Type *",
      type: "select",
      required: true,
      defaultValue: tourType,
      onChange: (value: string) => setTourType(value),
      options: bookingTypes.map((type) => ({
        value: type.id.toString(),
        label: type.tour_type,
      })),
      loading: bookingTypesQuery.isLoading,
    },
    {
      id: "theme_name",
      label: "Theme Name *",
      type: "text",
      required: true,
      defaultValue: themeName,
      onChange: (value: string) => setThemeName(value),
      placeholder: "Enter theme name",
    },
    {
      id: "meta_title",
      label: "Meta Title",
      type: "text",
      required: false,
      defaultValue: metaTitle,
      onChange: (value: string) => setMetaTitle(value),
      placeholder: "Enter meta title",
    },
    {
      id: "meta_description",
      label: "Meta Description",
      type: "textarea",
      required: false,
      defaultValue: metaDescription,
      onChange: (value: string) => setMetaDescription(value),
      placeholder: "Enter meta description",
    },
    {
      id: "meta_keywords",
      label: "Meta Keywords",
      type: "text",
      required: false,
      defaultValue: metaKeywords,
      onChange: (value: string) => setMetaKeywords(value),
      placeholder: "Enter meta keywords (comma separated)",
    },
  ];

  return (
    <div className="p-6">
      <div className="mt-5">
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            onClick={() => {
              setCurrentTheme(null);
              setIsModalOpen(true);
            }}
            icon={<Plus className="w-4 h-4" />}
            iconPosition="left"
          >
            Add Tour Theme
          </Button>

          <Button
            size="sm"
            onClick={handlePublish}
            icon={<UploadIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0 || updateStatus.isPending}
          >
            {updateStatus.isPending ? "Publishing..." : "Publish"}
          </Button>

          <Button
            size="sm"
            onClick={handleUnpublish}
            icon={<DownloadCloudIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0 || updateStatus.isPending}
          >
            {updateStatus.isPending ? "Unpublishing..." : "Unpublish"}
          </Button>

          <Button
            size="sm"
            variant="danger"
            onClick={handleDelete}
            icon={<TrashIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={
              selectedRows.length === 0 || deleteTourThemes.isPending
            }
          >
            {deleteTourThemes.isPending ? "Deleting..." : "Delete"}
          </Button>
        </div>

        <BookingTable
          data={tableData}
          columns={columns}
          heading={[]}
          searchable={true}
          filterable={true}
          sortable={true}
          selectable={true}
          onSelectionChange={setSelectedRows}
          loading={
            getTourThemes.isPending || bookingTypesQuery.isLoading
          }
          renderHeader={() => (
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={
                      selectedRows.length === tableData.length &&
                      tableData.length > 0
                    }
                    onChange={handleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  S.No
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tour Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Theme Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Meta Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Meta Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Meta Keywords
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
          )}
        />
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => {
              setIsModalOpen(false);
              setCurrentTheme(null);
            }}
          />

          <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
            <div
              className="relative bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-2xl mx-2 sm:mx-4 my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] flex flex-col transform transition-all duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
                  {currentTheme ? "Update Tour Theme" : "Add Tour Theme"}
                </h2>

                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setCurrentTheme(null);
                  }}
                  className="group p-1.5 sm:p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 flex-shrink-0"
                  aria-label="Close modal"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
                </button>
              </div>

              <div className="px-4 py-3 sm:px-5 sm:py-4 overflow-y-auto flex-1 min-h-0">
                <form onSubmit={handleFormSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 gap-4">
                    {formFields.map((field) => (
                      <div key={field.id} className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          {field.label}
                        </label>
                        {field.type === "select" ? (
                          <div className="relative">
                            <select
                              value={field.defaultValue || ""}
                              onChange={(e) => field.onChange(e.target.value)}
                              required={field.required}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              disabled={field.loading}
                            >
                              <option value="">
                                Select {field.label.replace("*", "")}
                              </option>
                              {field?.options?.map((option: any) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            {field.loading && (
                              <div className="absolute right-3 top-2.5">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                              </div>
                            )}
                          </div>
                        ) : field.type === "textarea" ? (
                          <textarea
                            value={field.defaultValue || ""}
                            onChange={(e) => field.onChange(e.target.value)}
                            placeholder={field.placeholder}
                            required={field.required}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                          />
                        ) : (
                          <input
                            type={field.type}
                            value={field.defaultValue || ""}
                            onChange={(e) => field.onChange(e.target.value)}
                            placeholder={field.placeholder}
                            required={field.required}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-end pt-4">
                    <button
                      type="submit"
                      disabled={
                        addTourTheme.isPending ||
                        updateTourTheme.isPending ||
                        bookingTypesQuery.isLoading
                      }
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {addTourTheme.isPending || updateTourTheme.isPending
                        ? "Processing..."
                        : currentTheme
                        ? "Update Tour Theme"
                        : "Add Tour Theme"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TourType;